import { useState, useCallback, useRef, useEffect, useMemo } from 'react'
import { FormattedMessage } from 'react-intl'

import { sendChooseEmotionEvent } from 'Analitics'
import Button from 'Components/Button'
import { RestartIcon } from 'Icons'

import DownloadButton from './DownloadButton'
import InfoDialog from './InfoDialog'
import MobileSearchButton from './MobileSearchButton'
import MobileSearchDialog from './MobileSearchDialog'
import Search from './Search'
import useWheelItemsData from './useWheelItemsData'
import Wheel from './Wheel'
import type { WheelItem } from './wheelData'
import WheelDescription, { EmotionDialog } from './WheelDescription'

const DEFAULT_ACTIVE_ITEMS: Set<WheelItem> = new Set()
const MAX_ITEMS = 7

const useMobileQuery = (): MediaQueryList | null => {
  return useMemo(() => {
    if (typeof window !== 'undefined') {
      return window.matchMedia('(max-width: 64rem)')
    }
    return null
  }, [])
}

export default function WheelBlock() {
  const mobileQuery = useMobileQuery()
  const [isMobile, setIsMobile] = useState(mobileQuery?.matches ?? false)
  const [searchText, setSearchText] = useState<string>('')
  const [activeItems, setActiveItems] = useState<Set<WheelItem>>(DEFAULT_ACTIVE_ITEMS)
  const infoDialogRef = useRef<HTMLDialogElement>(null)
  const [emotionDialogOpen, setEmotionDialogOpen] = useState(false)

  const [searchDialogOpen, setSearchDialogOpen] = useState(false)

  const openSearchDialog = useCallback(() => {
    setSearchDialogOpen(true)
  }, [])
  const closeSearchDialog = useCallback(() => {
    setSearchDialogOpen(false)
  }, [])

  const wheelItemsData = useWheelItemsData()

  function handleScreenChange(e: MediaQueryListEvent) {
    setIsMobile(e.matches)
  }

  useEffect(() => {
    if (mobileQuery) {
      mobileQuery.addEventListener('change', handleScreenChange)
      return () => {
        mobileQuery.removeEventListener('change', handleScreenChange)
      }
    }
  }, [mobileQuery])

  const itemOnSelect = useCallback(
    (item: WheelItem) => {
      if (isMobile) {
        setActiveItems((prev) => {
          const newSet = new Set<WheelItem>()
          if (!prev.has(item)) {
            newSet.add(item)
            sendChooseEmotionEvent({ emotion: item })
          }
          return newSet
        })
        setEmotionDialogOpen(true)
      } else {
        // desktop code
        setActiveItems((prev) => {
          let newSet = new Set(prev)
          if (prev.has(item)) {
            newSet.delete(item)
          } else if (prev.size < MAX_ITEMS) {
            newSet = new Set([item, ...prev])
            sendChooseEmotionEvent({ emotion: item })
          } else {
            infoDialogRef.current?.showModal()
          }
          return newSet
        })
      }
    },
    [isMobile],
  )

  const onClear = useCallback(() => {
    setActiveItems(DEFAULT_ACTIVE_ITEMS)
    setEmotionDialogOpen(false)
  }, [])

  if (!wheelItemsData) {
    return <Spinner />
  }

  return (
    <>
      <div className="relative flex grow flex-col justify-center gap-6 p-4 pt-0 md:flex-row md:py-6 lg:py-8">
        <div className="flex flex-col items-center gap-4">
          {!isMobile && (
            <div className="flex flex-row gap-2 self-stretch">
              <Search value={searchText} onChange={setSearchText} />
              <ResetButton onClear={onClear} clearEnabled={activeItems.size > 0} />
            </div>
          )}
          <div className="relative flex flex-col items-start gap-4 self-end">
            <Wheel
              activeItems={activeItems}
              itemOnSelect={itemOnSelect}
              wheelItemsData={wheelItemsData}
              searchText={searchText}
            />
            {isMobile && (
              <div className="absolute end-0 top-0">
                <MobileSearchButton onClick={openSearchDialog} />
              </div>
            )}
          </div>
          <div className="self-start">
            <DownloadButton />
          </div>
        </div>
        {!isMobile && (
          <WheelDescription activeItems={activeItems} wheelItemsData={wheelItemsData} />
        )}
      </div>
      <InfoDialog maxItems={MAX_ITEMS} dialogRef={infoDialogRef} />
      {isMobile && (
        <>
          <MobileSearchDialog
            isOpen={searchDialogOpen}
            wheelItemsData={wheelItemsData}
            itemOnSelect={itemOnSelect}
            onClose={closeSearchDialog}
          />
          <EmotionDialog
            isOpen={emotionDialogOpen}
            wheelItemsData={wheelItemsData}
            activeItem={Array.from(activeItems)[0] || null}
            onClose={onClear}
          />
        </>
      )}
    </>
  )
}

function Spinner() {
  return (
    <div className="flex grow justify-center py-17">
      <span className="loading loading-spinner loading-xl text-violet-300" />
    </div>
  )
}

type ResetButtonProps = {
  onClear: () => void
  clearEnabled: boolean
}

function ResetButton({ onClear, clearEnabled }: ResetButtonProps) {
  return (
    <Button id="reset-button" onClick={onClear} disabled={!clearEnabled}>
      <RestartIcon />
      <FormattedMessage id="buttons.clearAll" defaultMessage="Clear All" />
    </Button>
  )
}
